import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { extractError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowsOut, YoutubeLogo, CaretDown, CaretUp } from "@phosphor-icons/react";
import { toast } from "sonner";

// Extract YouTube video ID from various URL formats
function extractYoutubeId(url) {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m && m[1]) return m[1];
  }
  return null;
}

export default function ToolViewer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tool, setTool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [howToOpen, setHowToOpen] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/tools/${id}/html`);
        setTool(data);
      } catch (err) {
        toast.error(extractError(err));
        navigate("/library");
      } finally { setLoading(false); }
    })();
  }, [id, navigate]);

  const openStandalone = () => {
    const w = window.open("", "_blank");
    if (w) { w.document.open(); w.document.write(tool.html); w.document.close(); }
  };

  if (loading) return <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center"><div className="text-muted-foreground font-mono text-sm">Loading tool...</div></div>;
  if (!tool) return null;

  const videoId = extractYoutubeId(tool.video_url);

  return (
    <div data-testid="tool-viewer-page" className="min-h-[calc(100vh-4rem)] px-4 md:px-6 py-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <Link to="/library" data-testid="tool-back-btn" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary"><ArrowLeft size={16} weight="bold" /> Back to library</Link>
          <Button onClick={openStandalone} size="sm" variant="outline" data-testid="tool-fullscreen-btn" className="rounded-sm gap-2"><ArrowsOut size={14} weight="bold" /> Open standalone</Button>
        </div>

        {videoId && (
          <div data-testid="tool-howto-section" className="mb-4 border border-primary/40 bg-primary/5 rounded-sm overflow-hidden">
            <button
              type="button"
              onClick={() => setHowToOpen((v) => !v)}
              data-testid="tool-howto-toggle"
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-primary/10 transition-colors"
              aria-expanded={howToOpen}
            >
              <div className="flex items-center gap-2 text-left">
                <YoutubeLogo size={22} weight="fill" className="text-primary" />
                <div>
                  <div className="text-xs font-mono uppercase tracking-[0.2em] text-primary">Tutorial</div>
                  <div className="font-display font-bold text-base">How to use this tool</div>
                </div>
              </div>
              {howToOpen ? <CaretUp size={18} weight="bold" className="text-muted-foreground" /> : <CaretDown size={18} weight="bold" className="text-muted-foreground" />}
            </button>
            {howToOpen && (
              <div className="border-t border-primary/30 bg-black">
                <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
                  <iframe
                    data-testid="tool-howto-iframe"
                    className="absolute top-0 left-0 w-full h-full"
                    src={`https://www.youtube.com/embed/${videoId}?rel=0`}
                    title={`${tool.name} — tutorial`}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                </div>
              </div>
            )}
          </div>
        )}

        <div className="border border-border rounded-sm overflow-hidden bg-card">
          <div className="bg-black/70 border-b border-border px-4 py-2 flex items-center gap-3">
            <div className="flex gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-destructive/70" /><div className="w-2.5 h-2.5 rounded-full bg-yellow-500/70" /><div className="w-2.5 h-2.5 rounded-full bg-primary/70" /></div>
            <span className="text-xs font-mono text-muted-foreground truncate flex-1">terminal · {tool.slug}</span>
            <span className="text-xs font-mono text-primary">● LIVE</span>
          </div>
          <iframe data-testid="tool-iframe" title={tool.name} srcDoc={tool.html} sandbox="allow-scripts allow-forms allow-popups allow-modals" className="w-full bg-white" style={{ height: "calc(100vh - 12rem)", minHeight: "600px" }} />
        </div>
      </div>
    </div>
  );
}
